/**
 * useShuffledAvatars — assigns each link a robot avatar and reshuffles
 * every 60 seconds. Behaviors baked in:
 *
 *   * Initial assignment: permutation so 4 visible cards never share
 *     an avatar (pool of 8).
 *   * Reshuffle: every 60 s. Never re-assigns a card to its previous
 *     avatar (see assignAvatars for the swap trick).
 *   * Tab hidden: interval is paused via visibilitychange so background
 *     tabs stop burning JS timers. On re-focus we don't force an
 *     immediate shuffle — the next natural tick handles it.
 *   * prefers-reduced-motion: no shuffle at all; the initial assignment
 *     stays for the session.
 *
 * Return value: ``{ [linkId]: avatarIndex }``. Consumers read the index
 * against ``AVATARS`` from lib/avatars.js.
 */
import { useEffect, useRef, useState } from "react";
import { AVATARS, assignAvatars } from "@/lib/avatars";

// Feb-2026 update: shuffle interval bumped 60s → 1h per user request.
// Event-driven mood/reaction switches remain REALTIME (fire instantly
// on every ±1) — only the passive idle rotation slows to hourly so the
// avatars feel present without churning.
const RESHUFFLE_MS = 60 * 60 * 1000;

function detectReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export default function useShuffledAvatars(linkIds) {
  const [assignment, setAssignment] = useState(() => assignAvatars(linkIds));
  const reduced = useRef(detectReducedMotion());
  const idsRef = useRef(linkIds);

  // Keep the ref in sync so the interval callback reads the freshest
  // link list (Dashboard order changes on drag / add / delete).
  idsRef.current = linkIds;

  // Whenever the set of linkIds changes shape (add / delete), do a
  // one-off top-up so the new card gets an avatar immediately. We do
  // NOT wait for the next 60s tick — new cards would flicker
  // otherwise.
  useEffect(() => {
    setAssignment((prev) => {
      const same =
        linkIds.length === Object.keys(prev).length &&
        linkIds.every((id) => id in prev);
      if (same) return prev;
      return assignAvatars(linkIds, prev);
    });
  }, [linkIds.join("|")]);

  // Reshuffle timer — respects reduced-motion + tab visibility.
  useEffect(() => {
    if (reduced.current || AVATARS.length < 2) return undefined;

    let intervalId = null;
    const tick = () => {
      setAssignment((prev) => assignAvatars(idsRef.current, prev));
    };
    const start = () => {
      if (intervalId != null) return;
      intervalId = window.setInterval(tick, RESHUFFLE_MS);
    };
    const stop = () => {
      if (intervalId == null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, []);

  return assignment;
}
